import * as THREE from "three";
import {
  MODEL_MOON_ORBIT_RADIUS, MODEL_MOON_RADIUS, MODEL_SUN_RADIUS, REAL_UNITS_PER_AU, bodyInfo, eclToScene, heliocentricEcliptic, logLerp,
  modelBodyRadius, modelPosition, moonGeocentric, realBodyRadius, type BodyId, type Vec3,
} from "@solar/ephemeris";

/** 月球到地球的真实平均距离（AU）。 */
const MOON_MEAN_AU = 0.00257;
/** 模型模式下，地心向量（AU）→ 显示（场景单位）的放大系数。 */
export const MOON_K_MODEL = MODEL_MOON_ORBIT_RADIUS / MOON_MEAN_AU;
export const MOON_K_REAL = REAL_UNITS_PER_AU;
/** 月球轨道放大系数随模式插值（对数）。 */
export const moonK = (m: number) => logLerp(MOON_K_MODEL, MOON_K_REAL, m);

/** 显示半径：模型（压缩）与真实之间按对数插值。 */
export function displayRadius(id: BodyId, m: number): number {
  const model = id === "sun" ? MODEL_SUN_RADIUS : id === "moon" ? MODEL_MOON_RADIUS : modelBodyRadius(bodyInfo(id).radiusKm);
  const real = realBodyRadius(bodyInfo(id).radiusKm);
  return logLerp(model, real, m);
}

const v3 = (a: [number, number, number]) => new THREE.Vector3(a[0], a[1], a[2]);
const scaleVec = (v: Vec3, k: number): Vec3 => ({ x: v.x * k, y: v.y * k, z: v.z * k });

/** 日心向量（AU，黄道）→ 绝对显示坐标（按 m 在模型与真实之间线性插值）。 */
export function blendPosition(helio: Vec3, m: number): THREE.Vector3 {
  const model = v3(eclToScene(modelPosition(helio)));
  if (m <= 0) return model;
  const real = v3(eclToScene(scaleVec(helio, REAL_UNITS_PER_AU)));
  return model.multiplyScalar(1 - m).add(real.multiplyScalar(m));
}

/** 一帧内所有天体的绝对显示坐标。月球 = 显示后的地球 + 放大后的地心向量。 */
export function computeWorlds(date: Date, m: number, out: Map<BodyId, THREE.Vector3>, ids: readonly BodyId[]): void {
  for (const id of ids) {
    if (id === "sun") { (out.get(id) ?? out.set(id, new THREE.Vector3()).get(id)!).set(0, 0, 0); continue; }
    if (id === "moon") continue;
    const p = blendPosition(heliocentricEcliptic(id, date), m);
    const slot = out.get(id);
    if (slot) slot.copy(p); else out.set(id, p);
  }
  if (ids.includes("moon")) {
    const earth = out.get("earth") ?? blendPosition(heliocentricEcliptic("earth", date), m);
    const off = v3(eclToScene(scaleVec(moonGeocentric(date), moonK(m))));
    const p = earth.clone().add(off);
    const slot = out.get("moon");
    if (slot) slot.copy(p); else out.set("moon", p);
  }
}

/** 轨道折线（绝对显示坐标，float64）。月球轨道叠加到显示后的地球上。 */
export function orbitWorldPoints(points: Vec3[], m: number, moonCenter?: THREE.Vector3): Float64Array {
  const out = new Float64Array(points.length * 3);
  const k = moonCenter ? moonK(m) : 0;
  points.forEach((p, i) => {
    let v: THREE.Vector3;
    if (moonCenter) v = v3(eclToScene(scaleVec(p, k))).add(moonCenter);
    else v = blendPosition(p, m);
    out[i * 3] = v.x; out[i * 3 + 1] = v.y; out[i * 3 + 2] = v.z;
  });
  return out;
}
